<template>
  <v-app-bar app color="primary" dark dense>
    <v-app-bar-nav-icon @click="sidebarOpen = !sidebarOpen"></v-app-bar-nav-icon>
    <div style="height: 50px; width: 100px">
      <Logo :loading="false" @click="router.push('/home')"/>
    </div>
    <v-toolbar-title class="text-left">{{ APP_NAME }}</v-toolbar-title>

    <v-spacer></v-spacer>

    <v-btn icon @click="goTo('/home')">
      <v-icon>mdi-home</v-icon>
    </v-btn>

    <!--v-btn icon @click="goTo('/account')">
      <v-avatar :class="globalStore.currentGroup.color">
        <v-icon>{{ globalStore.currentGroup.icon }}</v-icon>
      </v-avatar>
    </v-btn-->
    <v-menu min-width="200px" v-if="user">
      <template v-slot:activator="{ props }">
        <v-btn
            icon
            v-bind="props"
        >
          <v-avatar
              :color="getColorFromName(user.user_metadata.username)"
              size="large"
          >
            <span class="text-h5">{{ getInitials(user.user_metadata.username) }}</span>
          </v-avatar>
        </v-btn>
      </template>
      <v-card>
        <v-card-text>
          <div class="mx-auto text-center">
            <v-avatar
                :color="getColorFromName(user.user_metadata.username)"
            >
              <span class="text-h5">{{ getInitials(user.user_metadata.username) }}</span>
            </v-avatar>
            <h3>{{ user.user_metadata.username }}</h3>
            <p class="text-caption mt-1">
              {{ user.email }}
            </p>
            <v-divider class="my-3"></v-divider>
            <v-btn
                variant="text"
                rounded
                @click="goTo('/account')"
            >
              Profil
            </v-btn>
            <v-divider class="my-3"></v-divider>
            <v-btn
                variant="text"
                rounded
                @click="useAuth().signOut"
            >
              Abmelden
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>

    <v-btn v-else @click="goTo('/login')" variant="outlined" size="small">
      <v-icon start>mdi-login</v-icon>
      Anmelden
    </v-btn>


    <v-btn icon @click="goTo('/settings')">
      <v-icon>mdi-cog</v-icon>
    </v-btn>
  </v-app-bar>

  <v-navigation-drawer v-model="sidebarOpen" app temporary>
    <!-- Schöner Close Button oben rechts -->
    <div class="d-flex justify-space-between align-center pa-3 pb-2">
      <span class="text-subtitle-1 font-weight-medium">Navigation</span>
      <v-btn
          icon
          size="small"
          variant="text"
          @click="sidebarOpen = false"
      >
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>
    </div>
    <v-divider></v-divider>

    <!-- TreeView Navigation -->
    <div style="max-width: 300px" class="pa-2">
      <v-treeview
          v-model:opened="openedNodes"
          v-model:activated="activatedNodes"
          :items="navigationItems"
          density="compact"
          item-value="id"
          item-title="title"
          activatable
          open-on-click
          indent-lines
          separate-roots
          @update:activated="handleActivation"
      >
        <template v-slot:prepend="{ item, isOpen }">
          <v-tooltip location="right" :disabled="!item.tooltip" :open-delay="500">
            <template v-slot:activator="{ props }">
              <!-- Composite icon: folder with badge overlay -->
              <span v-if="item.badgeIcon && item.children" v-bind="props" class="composite-icon">
                <v-icon :icon="isOpen ? 'mdi-folder-open' : 'mdi-folder'" size="20" />
                <v-icon :icon="item.badgeIcon" size="13" class="composite-icon__badge" />
              </span>
              <v-icon
                  v-else
                  v-bind="props"
                  :icon="getIcon(item, isOpen)"
                  size="20"
              ></v-icon>
            </template>
            <span>{{ item.tooltip }}</span>
          </v-tooltip>
        </template>

        <template v-slot:title="{ item }">
          <v-tooltip location="right" :disabled="!item.tooltip" :open-delay="500">
            <template v-slot:activator="{ props }">
              <span
                  v-bind="props"
                  :class="{ 'font-weight-bold': item.children }"
                  class="item-title"
              >
                {{ item.title }}
              </span>
            </template>
            <span>{{ item.tooltip }}</span>
          </v-tooltip>
        </template>
      </v-treeview>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import {useRoute, useRouter} from "vue-router";
import {ref, watch} from "vue";
import Logo from "@/components/visual/Logo.vue";
import {APP_NAME, getColorFromName, getInitials, useGlobalSetting} from "@/lib/utils";
import {useAuth} from "@/lib/useAuth";

const router = useRouter();
const route = useRoute();

const sidebarOpen = ref<boolean>(false);
const globalStore = useGlobalSetting();

const openedNodes = ref<string[]>([]);
const activatedNodes = ref<string[]>([]);

const {user} = useAuth();

interface NavItem {
  id: string;
  title: string;
  icon?: string;
  /** Small overlay icon displayed as badge on top of the folder icon */
  badgeIcon?: string;
  to?: string;
  tooltip?: string;
  children?: NavItem[];
}

const navigationItems: NavItem[] = [
  {
    id: 'home',
    title: 'Home',
    icon: 'mdi-home',
    to: '/home',
    tooltip: 'Zur Startseite'
  },
  {
    id: 'motorenliste',
    title: 'Motorenliste',
    icon: 'mdi-engine',
    to: '/motorenliste',
    tooltip: 'Motorenverwaltung'
  },
  {
    id: 'orders',
    title: 'Aufträge',
    icon: 'mdi-folder',
    badgeIcon: 'mdi-file-document-multiple',
    tooltip: 'Auftragsverwaltung',
    children: [
      {
        id: 'orders-all',
        title: 'Alle Aufträge',
        icon: 'mdi-file-document-multiple',
        to: '/orders',
        tooltip: 'Alle Aufträge anzeigen'
      },
      {
        id: 'orders-progress',
        title: 'In Bearbeitung',
        icon: 'mdi-file-edit',
        to: '/orders/progress',
        tooltip: 'Aufträge in Bearbeitung'
      },
      {
        id: 'orders-finished',
        title: 'Abgeschlossen',
        icon: 'mdi-file-check',
        to: '/orders/finished',
        tooltip: 'Abgeschlossene Aufträge'
      },
      {
        id: 'orders-failed',
        title: 'Unerledigt Beendet',
        icon: 'mdi-message-alert',
        to: '/orders/failed',
        tooltip: 'Unerledigt beendete Aufträge'
      }
    ]
  },
  {
    id: 'aufgaben',
    title: 'Aufgaben',
    icon: 'mdi-folder',
    badgeIcon: 'mdi-clipboard-list',
    tooltip: 'Aufgabenverwaltung',
    children: [
      {
        id: 'tasks-all',
        title: 'Alle Aufgaben',
        icon: 'mdi-clipboard-list',
        to: '/tasks',
        tooltip: 'Alle Aufgaben anzeigen'
      },
      {
        id: 'tasks-mine',
        title: 'Meine Aufgaben',
        icon: 'mdi-clipboard-account',
        to: '/tasks/mine',
        tooltip: 'Meine zugewiesenen Aufgaben'
      },
      {
        id: 'tasks-overdue',
        title: 'Überfällige',
        icon: 'mdi-clipboard-alert',
        to: '/tasks/overdue',
        tooltip: 'Überfällige Aufgaben'
      }
    ]
  },
  {
    id: 'lockouts',
    title: 'Freischaltungen',
    icon: 'mdi-folder',
    badgeIcon: 'mdi-lightning-bolt',
    tooltip: 'Freischaltaufgaben',
    children: [
      {
        id: 'lockouts-all',
        title: 'Alle Freischaltungen',
        icon: 'mdi-home-lightning-bolt-outline',
        to: '/lockouts',
        tooltip: 'Alle Freischaltungen anzeigen'
      },
      {
        id: 'lockouts-mine',
        title: 'Meine Freischaltungen',
        icon: 'mdi-lightning-bolt',
        to: '/lockouts/mine',
        tooltip: 'Meine zugewiesenen Freischaltungen'
      },
      {
        id: 'lockouts-map',
        title: 'Freischaltplan',
        icon: 'mdi-map',
        to: '/lockouts/map',
        tooltip: 'Freischaltbereiche auf dem Anlagenplan'
      }
    ]
  },
  /*{
    id: 'reporting',
    title: 'Reporting',
    icon: 'mdi-folder',
    tooltip: 'Berichte und Auswertungen',
    children: [
      {
        id: 'reporting-overview',
        title: 'Übersicht',
        icon: 'mdi-view-dashboard',
        to: '/reporting',
        tooltip: 'Reporting-Übersicht'
      },
      {
        id: 'reporting-orders',
        title: 'Auftragsbericht',
        icon: 'mdi-chart-box',
        to: '/reporting/orders',
        tooltip: 'Detaillierter Auftragsbericht'
      },
      {
        id: 'reporting-tasks',
        title: 'Aufgabenbericht',
        icon: 'mdi-chart-timeline',
        to: '/reporting/tasks',
        tooltip: 'Detaillierter Aufgabenbericht'
      },
      {
        id: 'reporting-freischaltungen',
        title: 'Freischaltbericht',
        icon: 'mdi-chart-line',
        to: '/reporting/freischaltungen',
        tooltip: 'Freischaltberichte anzeigen'
      }
    ]
  },*/
      {
    id: 'recourses',
    title: 'Ressourcen',
    icon: 'mdi-timetable',
    badgeIcon: 'mdi-timetable',
    tooltip: 'Planung und Übersicht von Ressourcen',
    children: [
      {
        id: 'staff_scheduling',
        title: 'Personalplanung',
        icon: 'mdi-timetable',
        to: '/staffScheduling',
        tooltip: 'Personalplanung anzeigen'
      },
      {
        id: 'wartungsplanung',
        title: 'Wartungsplanung',
        icon: 'mdi-wrench-clock',
        to: '/test',
        tooltip: 'Wartungsplanung anzeigen'
      },
    ]
  },
  {
    id: 'checklists',
    title: 'Checklisten',
    icon: 'mdi-folder',
    badgeIcon: 'mdi-playlist-edit',
    tooltip: 'Checklisten-Verwaltung',
    children: [
      {
        id: 'checklists-templates',
        title: 'Vorlagen',
        icon: 'mdi-playlist-edit',
        to: '/checklists/templates',
        tooltip: 'Checklisten-Vorlagen verwalten'
      },
      {
        id: 'checklists-all',
        title: 'Alle',
        icon: 'mdi-list-status',
        to: '/checklists/all',
        tooltip: 'Alle Checklisten ansehen'
      },
      {
        id: 'checklists-mine',
        title: 'Meine',
        icon: 'mdi-list-status',
        to: '/checklists/mine',
        tooltip: 'Meine Checklisten ansehen'
      }
    ]
  },
  {
    id: 'outages',
    title: 'Stillstände',
    icon: 'mdi-folder',
    badgeIcon: 'mdi-power',
    tooltip: 'Berichte und Auswertungen',
    children: [
      {
        id: 'outages-overview',
        title: 'Übersicht',
        icon: 'mdi-power',
        to: '/outages',
        tooltip: 'Stillstands Übersicht'
      },
    ]
  }
];

// Icon basierend auf Item-Typ und Zustand
const getIcon = (item: NavItem, isOpen: boolean): string => {
  if (!item.children) {
    return item.icon || 'mdi-file-document-outline';
  }
  return isOpen ? 'mdi-folder-open' : 'mdi-folder';
};

// Finde das NavItem basierend auf der ID
const findItemById = (items: NavItem[], id: string): NavItem | undefined => {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findItemById(item.children, id);
      if (found) return found;
    }
  }
  return undefined;
};

// Handle activation (Click auf TreeView Item)
const handleActivation = (ids: string[]) => {
  if (ids.length > 0) {
    const item = findItemById(navigationItems, ids[0]);
    if (item?.to) {
      navigateTo(item.to);
    }
  }
};

// Setze aktiven Node basierend auf aktueller Route
watch(() => route.path, (newPath) => {
  const findByPath = (items: NavItem[]): string | undefined => {
    for (const item of items) {
      if (item.to === newPath) return item.id;
      if (item.children) {
        const found = findByPath(item.children);
        if (found) return found;
      }
    }
    return undefined;
  };

  const activeId = findByPath(navigationItems);
  if (activeId) {
    activatedNodes.value = [activeId];
  }
}, {immediate: true});

const navigateTo = (path: string) => {
  router.push(path);
  sidebarOpen.value = false;
};

function goTo(path: string) {
  router.push(path);
}


</script>

<style scoped>
/* Treeview Container */
:deep(.v-treeview) {
  padding: 4px 0;
}

/* Item Styling - kleinere Schrift */
:deep(.v-treeview-item__label) {
  font-size: 0.875rem !important; /* 14px statt 16px */
  line-height: 1.4;
}

.item-title {
  font-size: 0.875rem !important;
}

/* Items linksbündig - Text direkt nach Icon */
:deep(.v-treeview-item) {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  margin-bottom: 0;
}

:deep(.v-treeview-item__content) {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  text-align: left !important;
}

:deep(.v-treeview-item__prepend) {
  margin: 0 !important;
  flex-shrink: 0;
}

:deep(.v-treeview-item__label) {
  text-align: left !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}

/* Durchgehende Linien - wie im Beispiel */
:deep(.v-treeview-item__children) {
  position: relative;
  margin-left: 10px;
  padding-left: 12px;
}

/* Vertikale Hauptlinie für Children */
:deep(.v-treeview-item__children::before) {
  content: '';
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: rgba(var(--v-theme-on-surface), 0.2);
}

/* Horizontale Linie zu jedem Child */
:deep(.v-treeview-item__children .v-treeview-item::before) {
  content: '';
  position: absolute;
  left: -12px;
  top: 50%;
  width: 12px;
  height: 1px;
  background-color: rgba(var(--v-theme-on-surface), 0.2);
}

/* Letztes Child - vertikale Linie nur bis zur Mitte */
:deep(.v-treeview-item__children .v-treeview-item:last-child::after) {
  content: '';
  position: absolute;
  left: -12px;
  top: 50%;
  bottom: -8px;
  width: 1px;
  background-color: var(--v-theme-surface);
  z-index: 1;
}

/* Aktive Items hervorheben */
:deep(.v-treeview-item--active) {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
  border-radius: 4px;
}

:deep(.v-treeview-item--active .v-treeview-item__label) {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

/* Hover Effekt */
:deep(.v-treeview-item:hover) {
  background-color: rgba(var(--v-theme-primary), 0.04);
  border-radius: 4px;
  cursor: pointer;
}

/* Icon Größe */
:deep(.v-treeview-item__prepend .v-icon) {
  font-size: 20px !important;
}

/* Expand/Collapse Icon Position */
:deep(.v-treeview-item__toggle) {
  margin-right: 4px;
}

/* Smooth Transitions */
:deep(.v-treeview-item),
:deep(.v-treeview-item__children) {
  transition: all 0.2s ease;
}

/* Indent-Lines Style anpassen */
:deep(.v-treeview-item--child) {
  position: relative;
}

/* Composite icon: folder with small badge overlay */
.composite-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.composite-icon__badge {
  position: absolute;
  bottom: -1px;
  right: -3px;
  color: rgb(var(--v-theme-primary));
  filter: drop-shadow(0 0 1px rgb(var(--v-theme-surface)));
}

/* Tooltip Styling */
:deep(.v-tooltip > .v-overlay__content) {
  background: rgba(var(--v-theme-on-surface), 0.9);
  font-size: 0.75rem;
  padding: 4px 8px;
}

/* Drawer Header Styling */
.v-navigation-drawer {
  border-right: thin solid rgba(var(--v-theme-on-surface), 0.12);
}
</style>